import { useState } from "react";
import { Building2, ChevronDown, ClipboardList, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function greetingFor(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export function localDateIso(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function weekOfSchoolYear(startIso, date = new Date()) {
  if (!startIso) return null;
  const start = new Date(`${startIso}T00:00:00Z`);
  const today = new Date(`${localDateIso(date)}T00:00:00Z`);
  const diff = Math.floor((today - start) / (7 * 24 * 60 * 60 * 1000));
  return diff >= 0 ? diff + 1 : null;
}

const Dot = () => (
  <span className="ops-dot" aria-hidden="true">
    ·
  </span>
);

const ClientFilter = ({ options, value, onChange, disabled }) => (
  <label className="ops-filter" data-testid="dashboard-client-filter-wrap">
    <span className="sr-only">Filter by client</span>
    <Building2 size={16} aria-hidden="true" />
    <select data-testid="dashboard-client-filter" disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">All clients</option>
      {options.map((client) => (
        <option key={client.id} value={client.id}>
          {client.name}
        </option>
      ))}
    </select>
    <ChevronDown size={16} aria-hidden="true" />
  </label>
);

export const DashboardHeader = ({ user, schoolYear, canCreate, clientOptions = [], clientId = "", onClientChange, filterDisabled }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const week = weekOfSchoolYear(schoolYear?.start);
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return (
    <header className="ops-header" data-testid="dashboard-header">
      <div className="ops-heading">
        <h1 className="ops-title" data-testid="dashboard-title">
          Operations overview
        </h1>
        <p className="ops-subtitle">
          <span data-testid="dashboard-greeting">
            {greetingFor()}, {user?.first_name}
          </span>
          <Dot />
          <span className="ops-subtitle-context" data-testid="dashboard-context">
            <span>School year {schoolYear?.label || "—"}</span>
            {week && (
              <>
                <Dot />
                <span>Week {week}</span>
              </>
            )}
            <Dot />
            <span>{today}</span>
          </span>
        </p>
      </div>
      <div className="ops-controls">
        {onClientChange && <ClientFilter options={clientOptions} value={clientId} onChange={onClientChange} disabled={filterDisabled} />}
        {canCreate && (
          <>
            <button
              className="ops-primary-button"
              data-testid="create-staffing-request-button"
              type="button"
              onClick={() => navigate("/staffing-requests")}
            >
              <Plus size={16} /> New request
            </button>
            <div className="ops-menu-wrap">
              <button
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label="More actions"
                className="ops-menu-button"
                data-testid="dashboard-more-actions"
                type="button"
                onClick={() => setOpen((value) => !value)}
              >
                <ChevronDown size={16} />
              </button>
              {open && (
                <div className="ops-menu" role="menu" data-testid="dashboard-actions-menu" onMouseLeave={() => setOpen(false)}>
                  <button
                    data-testid="create-evaluation-request-button"
                    role="menuitem"
                    type="button"
                    onClick={() => navigate("/evaluation-pipeline?new=1")}
                  >
                    <ClipboardList size={15} /> New evaluation request
                  </button>
                  <button data-testid="open-staffing-board-action" role="menuitem" type="button" onClick={() => navigate("/master-staffing-board")}>
                    Open staffing board
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};
